import React, { useContext, useState, useRef, useEffect, useCallback, useReducer } from 'react';
import axios from 'axios';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { Notifications, Help, ArrowDropDown, Upload, UploadFileOutlined, CloseIcon, CloseRounded, Business } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    Avatar,
    Button,
    Badge,
    Box,
    Select,
    Divider,
    Drawer,
    TextField,
    InputLabel,
    FormControl,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    Paper, Tabs,
    Tooltip
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import Tab from '@mui/material/Tab';
import { useNavigate } from 'react-router-dom';

import { formatDistanceToNow, parseISO, set } from "date-fns";

import PageLoader from './components/PageLoader';
import {
    ApiManager,
    TextAreaWithCount,
    TextFieldWithIconButton,
    DeterminePermissions2,
    getFirstAvailablePage,
    UtilityLibrary,
    DebounceForm,
    ValidateForm,
    sessionService,
    SessionManager,
    ConfirmationDialog,
    NotificationDialog,
    MessageBox,
    BusinessUtility,
    EnumUtility,
    LoggerUtility,
    validateToken
} from './components/ComponentsLibrary';
import { jwtDecode } from 'jwt-decode';

import { useAuthContext } from './functions/AuthContext';
import { MasterEnum } from './enums/MasterEnum';
import { useConfigContext } from './functions/ConfigContext';
import { ClientType } from './enums/ClientType';


const SiteSelector = ({ }) => {
    useEffect(() => {
        document.title = 'YTrac :: Site Selection';
    }, []); // Empty dependency array to set the title only on mount

    // Add this useEffect right after the existing one around line 808
    useEffect(() => {
        try {
            // 🔍 FIXED: Always clear selections when arriving at site selector
            const loginUserInfo = sessionService.getUser();
            if (loginUserInfo) {
                // Clear site-related session data
                const clearedUser = {
                    ...loginUserInfo,
                    stateID: "",
                    stateName: "",
                    siteID: "",
                    siteName: "",
                    timeZone: "",
                    isSiteSelected: false
                };
                sessionService.updateUser(clearedUser);
                
                // Clear site settings from session
                SessionManager.RemoveSession("SiteSettingsDetailsKey");
                SessionManager.RemoveSession("UserPermissions");
                
                console.log('🔍 Site selector: Cleared all previous selections for fresh start');
            }
            
            // Reset form to initial state
            setFormValues(initialValues);
            setSiteLists([]);
            setIsSubmitEnabled(false);
            
            SessionManager.SetSession(SessionManager.CurrentActivePageKey, "/siteselect");
            onPageReload();
        }
        catch (error) {
            LoggerUtility.ManageErrorLogging(error, errorFormName, "clear selections on mount");
        }
    }, []); // Run once on mount

    const navigate = useNavigate();

    const { configLibrary } = useConfigContext();

    // const { LoginUserInfo } = sessionService;

    const errorFormName = "Site Selection";

    const { setIsSiteSelected } = useAuthContext();

    // To-do: Modify Code - Change the hard-code value of Site Setting Details ID
    const documentPathID = EnumUtility.SiteSettingsDetail.DocumentPath.SettingsDetailID;
    const caseRegexSettingID = EnumUtility.SiteSettingsDetail.CaseNumberRegex.SettingsDetailID;
    const caseInsertAllowSettingID = EnumUtility.SiteSettingsDetail.CaseNumberInsertAllow.SettingsDetailID;
    const noteSettingID = EnumUtility.SiteSettingsDetail.NotesCharsLimit.SettingsDetailID;
    const narrativeSettingID = EnumUtility.SiteSettingsDetail.NarrativeCharsLimit.SettingsDetailID;
    const narrativeClipboardIndexSettingID = EnumUtility.SiteSettingsDetail.NarrativeCopyToClipboardIndex.SettingsDetailID;
    const narrativeClipboardKeywordsSettingID = EnumUtility.SiteSettingsDetail.NarrativeCopyToClipboardManageKeywords.SettingsDetailID;
    const softLockTimerSettingID = ClientType.SettingsDetail.UI_Miscellaneous__Index_Soft_lock_Timer; // setting 637
    const batchDocumentStaleLockTimeout = ClientType.SettingsDetail.UI_Miscellaneous__Batch_Document_Stale_Lock_Timeout; // setting 332
    const mdhsImportSweepProcessIDSettingID = ClientType.SettingsDetail.Import_Related__MDHS_Import_SweepProcessID; // setting 407
    const docResultsCapacitySettingID = EnumUtility.SiteSettingsDetail.DocumentSearchResultsCapacity.SettingsDetailID;
    const daysToNextFollowUpSettingID = EnumUtility.SiteSettingsDetail.DaysToNextFollowup.SettingsDetailID;
    const docViewerLicenseSettingID = EnumUtility.SiteSettingsDetail.DocViewerLicenseKey.SettingsDetailID;
    const docViewerPrintQualitySettingID = EnumUtility.SiteSettingsDetail.DocViewerPrintQuality.SettingsDetailID;
    const ApiWriteNarrativeSettingID = EnumUtility.SiteSettingsDetail.ApiWriteNarrative.SettingsDetailID;

    const multipart_Narrative_DelimiterSettingID = MasterEnum.SettingsDetail.UI_Miscellaneous__Multipart_Narrative_Delimiter;
    const multipart_Narrative_ConnectorSettingID = MasterEnum.SettingsDetail.UI_Miscellaneous__Multipart_Narrative_Connector;
    const localhostLicenseKey = "EejeUoGc1D9bR21dO9L_hhhsL7fBKxpwPmJ8MVOTwZeXhAGP0E4jtlf83KiMHWPiGXeeiDaeTxpc9Czx_BA4Q-WGcYLeWe0dpQzqYXCmEOM7TtY7Z9kZRwrsKqAwjrwFADSzVZwM1wATN47cL7hDRrRJwUg80YrNJjQ86jDbNQA3P01O7C6IPxcT1tHox9CmAUEtHmIee0QgvC7VGYdR7QEFD1ufJLgFeqLl0nG7w58AqJN0GjDAg5aRRo1hr6tD-yT8-BrVE4QiCV81UcyYSH4fKMvtnhyGYNwJ_qdyeIZiYE68cOQ9jlO9tJkK88Wkn4NeagMeZAcLRaH3i2-HTFbiuKgz6OCvFMnuB2ZSIDnlEk3WPvcnQeKFCM1_VtBt8OhzFFAoX2NAHITipXEbcB4o8-OgI24L0oBT5tgEIxXsvaodHbgu4TQbo-oqiJNp813gfXQZVkr8uNfHKIHxVfFWPSG5Cw378HZayFQYypLS3ihx4pct5tLg3AhSqQMS57C2v6YiXoHLpOyt2TNQkpLNzoPaaFC-aPvRumxsoiq8fm39NfdGDGB-cubRzOHn7SLkI2CY9XEZyI2Eh0j-vw=="


    const initialSiteSettingDetailValues = {
        DocumentPath: "",
        CaseNumberRegex: "",
        CaseNumberInsertAllow: "",
        NarrativeCharsLimit: "",
        NarrativeCopyToClipboardIndex: 1,
        NarrativeCopyToClipboardManageKeywords: 1,
        NotesCharsLimit: "",
        SoftLockTimer: 30,
        BatchDocumentStaleLockTimeout: 30,
        DocumentResultsCapacity: 0,
        DaysToNextFollowup: 0,
        DocViewerLicenseKey: "",
        MDHSImportSweepProcessID: 0,
        DocViewerPrintQuality: "HIGH",
        ApiWriteNarrative: 0,
    }

    const [siteSettingDetailValues, setSiteSettingDetailValues] = useState(initialSiteSettingDetailValues);

    /* 
        useEffect(() => {
            const handleUserInfoChange = (newUserInfo) => {
                setLoginUserInfo(newUserInfo); // Update state when UserInfo changes
            };
    
            sessionService.subscribe(handleUserInfoChange);
    
            return () => {
                sessionService.unsubscribe(handleUserInfoChange); // Cleanup on unmount
            };
        }, []);
     */
    const [localLoginUserInfo, setLocalLoginUserInfo] = useState(null);

    const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
    const [notificationDialogContent, setNotificationDialogContent] = useState([]);

    const [messageBoxContent, setMessageBoxContent] = useState([]);
    const [messageBoxVisible, setMessageBoxVisible] = useState(false);

    const [isPageLoading, setIsPageLoading] = useState(false);
    const backgroundImageUrl = '/images/Y-Trac-Landing-Page-Designs.png';

    // Get the authenticated user's email from JWT token
    const getEmailFromToken = () => {
        try {
            const token = sessionStorage.getItem("token");
            if (token) {
                const decodedToken = jwtDecode(token);
                return decodedToken.emails && decodedToken.emails.length > 0 ? decodedToken.emails[0] : "";
            }
            return "";
        } catch (error) {
            console.error("Error decoding token:", error);
            return "";
        }
    };

    const [selectedLoginUser, setSelectedLoginUser] = useState(getEmailFromToken());

    const [stateLists, setStateLists] = useState([]);
    /* const [selectedState, setSelectedState] = useState("");
    const [selectedStateName, setSelectedStateName] = useState(""); */

    const [siteLists, setSiteLists] = useState([]);
    /* const [selectedSite, setSelectedSite] = useState("");
    const [selectedSiteName, setSelectedSiteName] = useState(""); */

    const initialValues = {
        state_select: "",
        state_name_select: "",
        site_select: "",
        site_name_select: "",
        time_zone_select: ""
    };

    // Mapping for fields from both arrays
    const loginUserInfoMapping = {
        userInfoID: "userInfoID",
        legacyUserID: "legacyUserID",
        orgClientID: "orgClientID",
        stateID: "stateID",
        stateName: "stateName",
        siteID: "siteID",
        siteName: "siteName",
        roleID: "roleID",
        siteRoleID: "siteRoleID",
        statusID: "statusID",
        userName: "userName",
        firstName: "firstName",
        lastName: "lastName",
        isActive: "isActive",
        checkSiteChange: "checkSiteChange",
        isSiteSelected: "isSiteSelected",
        isTokenValidated: "isTokenValidated",
    };

    const getValidationRules = (values) => ({
        state_select: { required: true, validator: (value) => value !== "" },
        site_select: { required: true, validator: (value) => value !== "" },
        time_zone_select: { required: true, validator: (value) => value !== "" }
    });

    const [formValues, setFormValues] = useState(initialValues);
    const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

    // Debounced validation using the centralized validateForm
    const debouncedValidation = useCallback(
        DebounceForm((values) => {
            try {
                const rules = getValidationRules(values);
                const isValid = ValidateForm(values, rules);
                setIsSubmitEnabled(isValid);
            }
            catch (error) {
                throw LoggerUtility.ProcessErrorData(error, "debouncedValidation", {});
            }
        }, 300),
        []
    );

    // Validate on initial load
    useEffect(() => {
        try {
            debouncedValidation(formValues);
        }
        catch (error) {
            LoggerUtility.ManageErrorLogging(error, errorFormName, "debouncedValidation - page load");
        }
    }, []); // Run only once on mount

    const updateTokenValidationInSession = async (tokenValid) => {
        sessionService.updateUser({ isTokenValidated: tokenValid, });
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const updatedValues = {
            ...formValues,
            [name]: type === "checkbox" ? checked : value,
        };
        setFormValues(updatedValues);
        debouncedValidation(updatedValues);
    };

    const clearStateAndSiteValues = async () => {
        try {
            setStateLists([]);
            setSiteLists([]);
            UtilityLibrary.updateArrayStateValues(setFormValues, initialValues);
            /* setSelectedState("");
            setSelectedStateName("");
            setSelectedSite("");
            setSelectedSiteName(""); */
        } catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "clearStateAndSiteValues", {});
        }
    };

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const getSiteSettingDetails = async () => {
        try {
            const settingsDetailIDList = [documentPathID, caseRegexSettingID, caseInsertAllowSettingID, noteSettingID,
                narrativeSettingID, narrativeClipboardIndexSettingID, narrativeClipboardKeywordsSettingID, softLockTimerSettingID, batchDocumentStaleLockTimeout, docResultsCapacitySettingID,
                daysToNextFollowUpSettingID, docViewerLicenseSettingID, mdhsImportSweepProcessIDSettingID, docViewerPrintQualitySettingID, multipart_Narrative_DelimiterSettingID, 
                multipart_Narrative_ConnectorSettingID, ApiWriteNarrativeSettingID];

            // Master DB Schema - add setting value to the list to retrieve
            settingsDetailIDList.push(MasterEnum.SettingsDetail.Reporting_Related__Master_SQL_Server_Database_Schema);

            const params = {
                settingsDetailIDList: settingsDetailIDList.join(","),
                yTracSiteID: formValues.site_select
            }
            const siteSettingDetails = siteSettingDetailValues;
            const results = await ApiManager.GetSettingsDetailValuesFromApi(params)
            for (const result of results?.settingDetailsApiData || []) {
                const processed = await processSiteSettingResponse( result);  // Process the response once received
                Object.assign(siteSettingDetails, processed); 
            }

            // Master DB Schema - set value
            siteSettingDetails.masterDBSchema = results?.settingDetailsApiData?.find(
                x => x.settingsDetailID === '65'
            )?.settingsDetailValue || null;

            return siteSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getSiteSettingDetails", {});
        }
    };

    const processSiteSettingResponse = async (siteSettingApiData) => {
        try {
            switch (Number(siteSettingApiData?.settingsDetailID)) {
                case documentPathID:
                    return getDocPathFromSiteSetting(siteSettingApiData);

                case caseRegexSettingID:
                    return await getCaseRegexFromSiteSetting(siteSettingApiData);

                case caseInsertAllowSettingID:
                    return getCaseNumberInsertAllowFromSiteSetting(siteSettingApiData);

                case noteSettingID:
                    return getNoteCharsFromSiteSetting(siteSettingApiData);

                case narrativeSettingID:
                    return getNarrativeCharsFromSiteSetting(siteSettingApiData);

                case narrativeClipboardIndexSettingID:
                    return getNarrativeClipboardIndexFromSiteSetting(siteSettingApiData);

                case narrativeClipboardKeywordsSettingID:
                    return getNarrativeClipboardKeywordsFromSiteSetting(siteSettingApiData);

                case docResultsCapacitySettingID:
                    return getDocResultsCapacityFromSiteSetting(siteSettingApiData);

                case daysToNextFollowUpSettingID:
                    return getDaysToNextFromSiteSetting(siteSettingApiData);

                case docViewerLicenseSettingID:
                    return getDocViewerLicenseFromSiteSetting(siteSettingApiData);

                case softLockTimerSettingID:
                    return getSoftLockTimerFromSiteSetting(siteSettingApiData);

                case batchDocumentStaleLockTimeout:
                    return getBatchDocumentStaleLockTimeoutFromSiteSetting(siteSettingApiData);

                case mdhsImportSweepProcessIDSettingID:
                    return getMdhsImportSweepProcessIDFromSiteSetting(siteSettingApiData);

                case docViewerPrintQualitySettingID:
                    return getDocViewerPrintQualityFromSiteSetting(siteSettingApiData);

                case multipart_Narrative_DelimiterSettingID:
                    return getMultipartNarrativeDelimiterFromSiteSetting(siteSettingApiData);

                case multipart_Narrative_ConnectorSettingID:
                    return getMultipartNarrativeConnectorFromSiteSetting(siteSettingApiData);

                case ApiWriteNarrativeSettingID:
                    return getApiWriteNarrativeFromSiteSetting(siteSettingApiData);

                default:
                    console.warn(`Unhandled param: ${JSON.stringify(siteSettingApiData)}`);
            }
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "processSiteSettingResponse", {});
        }
    };

    // Method: To get the document path for the login site.
    const getDocPathFromSiteSetting = (siteSettingApiData) => {
        try {
            let docPath = "";
            docPath = siteSettingApiData?.settingsDetailValue || "";
            const updatedSettingDetails = {
                DocumentPath: docPath,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getDocPathFromSiteSetting", {});
        }
    };

    // Method: To get the Regular Expression Pattern for a Case Number for the login site.
    const getCaseRegexFromSiteSetting = async (siteSettingApiData) => {
        try {
            let regexPattern = "";
            let regexMessage = "";
            regexPattern = siteSettingApiData?.settingsDetailValue || "";
            //regexMessage = BusinessUtility.GetCaseRegexMessage(formValues.state_select);
        
            const loginUserInfo = sessionService.getUser();
            const params = {
                StateID: loginUserInfo?.stateID,
                MessageContentID: ClientType.MessageContent.V_DV3513_Case_Numbers_Must_Be_Formatted_As_Example,
            };
            
            const messageContentData = await ApiManager.GetMessageContentFromApi(params);
            regexMessage = messageContentData?.messageText || "Invalid Case Number format.";
            const updatedSettingDetails = {
                CaseNumberRegex: { caseRegexValue: regexPattern, caseRegexMessage: regexMessage },
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getCaseRegexFromSiteSetting", {});
        }
    };

    // Method: To get the access to whether insert the Case Number to the table when the case is not found.
    const getCaseNumberInsertAllowFromSiteSetting = (siteSettingApiData) => {
        try {
            let caseInsertAllow = 0;
            caseInsertAllow = siteSettingApiData?.settingsDetailValue || 0;
            const updatedSettingDetails = {
                CaseNumberInsertAllow: caseInsertAllow,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getCaseNumberInsertAllowFromSiteSetting", {});
        }
    };


    // Method: To get the maximum number of characters the string can be for the NARRATIVE for the login site.
    const getNarrativeCharsFromSiteSetting = (siteSettingApiData) => {
        try {
            let narrativeChars = 500;
            narrativeChars = siteSettingApiData?.settingsDetailValue || 500;
            //SessionManager.SetSession(SessionManager.NarrativeCharsLimitKey, narrativeChars);
            const updatedSettingDetails = {
                NarrativeCharsLimit: narrativeChars,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getNarrativeCharsFromSiteSetting", {});
        }
    };

    // Method: To get the access to copy of narrative text to the clipboard in index page for the login site.
    const getNarrativeClipboardIndexFromSiteSetting = (siteSettingApiData) => {
        try {
            let narrativeCopy = 1;
            narrativeCopy = siteSettingApiData?.settingsDetailValue || 1;
            const updatedSettingDetails = {
                NarrativeCopyToClipboardIndex: narrativeCopy,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getNarrativeClipboardIndexFromSiteSetting", {});
        }
    };

    // Method: To get the access to copy of narrative text to the clipboard in manage keywords page for the login site.
    const getNarrativeClipboardKeywordsFromSiteSetting = (siteSettingApiData) => {
        try {
            let narrativeCopy = 1;
            narrativeCopy = siteSettingApiData?.settingsDetailValue || 1;
            const updatedSettingDetails = {
                NarrativeCopyToClipboardManageKeywords: narrativeCopy,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getNarrativeClipboardKeywordsFromSiteSetting", {});
        }
    };


    // Method: To get the maximum number of characters the string can be for the NOTE for ACTION for the login site.
    const getNoteCharsFromSiteSetting = (siteSettingApiData) => {
        try {
            let noteChars = 500;
            noteChars = siteSettingApiData?.settingsDetailValue || 500;
            //SessionManager.SetSession(SessionManager.NotesCharsLimitKey, noteChars);
            const updatedSettingDetails = {
                NotesCharsLimit: noteChars,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getNoteCharsFromSiteSetting", {});
        }
    };

    // Method: To get the number of document search results to be shown for the login site.
    const getDocResultsCapacityFromSiteSetting = (siteSettingApiData) => {
        try {
            let docResultsCapacity = 0;
            docResultsCapacity = siteSettingApiData?.settingsDetailValue || 1;
            const updatedSettingDetails = {
                DocumentResultsCapacity: docResultsCapacity,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getDocResultsCapacityFromSiteSetting", {});
        }
    };

    // Method: To get the days to next follow up to be shown for the login site.
    const getDaysToNextFromSiteSetting = (siteSettingApiData) => {
        try {
            let daysToNext = 0;
            daysToNext = siteSettingApiData?.settingsDetailValue || 0;
            const updatedSettingDetails = {
                DaysToNextFollowup: daysToNext,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getDaysToNextFromSiteSetting", {});
        }
    };


    // Method: To get the License Key of the Document Viewer for the login site.
    const getDocViewerLicenseFromSiteSetting = (siteSettingApiData) => {
        try {
            let updatedSettingDetails = "";

            if (window.location.hostname.toLowerCase() === "localhost") {
                updatedSettingDetails = {
                    DocViewerLicenseKey: localhostLicenseKey
                };
            } else {
                let licenseKey = '';
                licenseKey = siteSettingApiData?.settingsDetailValue || "";
                updatedSettingDetails = {
                    DocViewerLicenseKey: licenseKey,
                };
            }

            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getDocViewerLicenseFromSiteSetting", {});
        }
    };

    // Method: To get the length of time for a soft lock on a batch
    const getSoftLockTimerFromSiteSetting = (siteSettingApiData) => {
        try {
            let timer = '';
            timer = siteSettingApiData?.settingsDetailValue || 30;
            const updatedSettingDetails = {
                SoftLockTimer: timer,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getSoftLockTimerFromSiteSetting", {});
        }
    };

        // Method: To get the length of time for a stale lock on a batch document
    const getBatchDocumentStaleLockTimeoutFromSiteSetting = (siteSettingApiData) => {
        try {
            let timer = '';
            timer = siteSettingApiData?.settingsDetailValue || 60;
            const updatedSettingDetails = {
                BatchDocumentStaleLockTimeout: timer,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getBatchDocumentStaleLockTimeoutFromSiteSetting", {});
        }
    };

    // Method: To get the SweepProcessID for the MDHS Import folder (used to conditionally retrieve document metadata)
    const getMdhsImportSweepProcessIDFromSiteSetting = (siteSettingApiData) => {
        try {
            const sweepProcessID = parseInt(siteSettingApiData?.settingsDetailValue, 10) || 0;
            const updatedSettingDetails = {
                MDHSImportSweepProcessID: sweepProcessID,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getMdhsImportSweepProcessIDFromSiteSetting", {});
        }
    };

           
    const getDocViewerPrintQualityFromSiteSetting = (siteSettingApiData) => {
        try {
            const printQuality = siteSettingApiData?.settingsDetailValue || "HIGH";
            const updatedSettingDetails = {
                DocViewerPrintQuality: printQuality,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getDocViewerPrintQualityFromSiteSetting", {});
        }
    };

    const getMultipartNarrativeDelimiterFromSiteSetting = (siteSettingApiData) => {
        try {
            const delimiter = siteSettingApiData?.settingsDetailValue || "";
            const updatedSettingDetails = {
                MultipartNarrativeDelimiter: delimiter,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getMultipartNarrativeDelimiterFromSiteSetting", {});
        }
    };

    const getMultipartNarrativeConnectorFromSiteSetting = (siteSettingApiData) => {
        try {
            const connector = siteSettingApiData?.settingsDetailValue || "";
            const updatedSettingDetails = {
                MultipartNarrativeConnector: connector,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getMultipartNarrativeConnectorFromSiteSetting", {});
        }
    };

        const getApiWriteNarrativeFromSiteSetting = (siteSettingApiData) => {
        try {
            const writeNarrativeToApi = siteSettingApiData?.settingsDetailValue || "0";
            const updatedSettingDetails = {
                ApiWriteNarrative: writeNarrativeToApi,
            };
            return updatedSettingDetails;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getApiWriteNarrativeFromSiteSetting", {});
        }
    };

    const onSubmitFormDetails = async (formDetails) => {
        try {
            let isSiteSelect = false;
            if ((formDetails.state_select !== "") && (formDetails.site_select !== "")) {
                setIsSiteSelected(true);
                isSiteSelect = true;
            }
            else {
                setIsSiteSelected(false);
            }
            sessionService.updateUser({ stateID: formDetails.state_select, stateName: formDetails.state_name_select, siteID: formDetails.site_select, siteName: formDetails.site_name_select, isSiteSelected: isSiteSelect, timeZone: formDetails.time_zone_select });
            const loginUserSession = await checkLoginUserSession();

            // 🔍 IMPORTANT: Get site settings first (this populates masterServerDBSchema)
            const siteSettingDetails = await getSiteSettingDetails();
            SessionManager.SetSession(SessionManager.SiteSettingsDetailsKey, JSON.stringify(siteSettingDetails));

            let integrationID = 0;
            const paramsIntegration = {
                yTracSiteID: formDetails.state_select,
                integrationTypeID: 4, // To-Do: Modify Code - Change the Hard-Coded Integration Type ID
            };

            const integrationIDfromApi = await ApiManager.GetIntegrationID(paramsIntegration);
            if (integrationIDfromApi.length > 0) {
                integrationID = integrationIDfromApi[0].integrationID;
            }

            SessionManager.SetSession(SessionManager.IntegrationIDKey, integrationID);
            // SessionManager.SetSession(SessionManager.CurrentActivePageKey, "/newdocument"); //RC remmoved it from here
            
            const paramsIntegrationInfo = {
                yTracSiteID: formDetails.site_select
            };
            
            const integrationInfofromApi = await ApiManager.GetIntegrationInfo(paramsIntegrationInfo);
            SessionManager.SetSession(SessionManager.IntegrationInfoKey, JSON.stringify(integrationInfofromApi));

            
            const loginUserInfo = sessionService.getUser();
            await setUserLoginDetails({ userLogin: null, userLoginID: loginUserInfo.legacyUserID });
            
            // 🔍 REMOVED: Don't redirect immediately - let handleSubmitClick handle permissions first
            // window.location.replace('/newdocument');

        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "onSubmitFormDetails", {});
        }
    };

    const getLoginUserDetails = async (loginUserEmail) => {
        try {
            let hasUserData = false;
            if (loginUserEmail !== "") {
                const loginUserData = await ApiManager.GetUserInfoByEmailFromApi(loginUserEmail);
                if (loginUserData) {
                    const targetObject = UtilityLibrary.mapApiResponseToObject(loginUserData, loginUserInfoMapping);
                    sessionService.setUser(targetObject);

                    // const userLoginData = await ApiManager.GetUserLoginFromApi({ userLogin: null, userLoginID: loginUserData.legacyUserID });

                    // SessionManager.SetSession(SessionManager.UserLoginDetailsKey, JSON.stringify(userLoginData));
                    await setUserLoginDetails({ userLogin: null, userLoginID: loginUserData.legacyUserID });
                    updateTokenValidationInSession(true);
                    //handleMessageBoxContent(EnumUtility.SiteSelectionAlerts.UserFound);
                    hasUserData = true;
                    await fetchStates(loginUserData.userInfoID, true);
                }
            }

            if (!hasUserData) {
                sessionService.clearUser();
                clearStateAndSiteValues();
                /* const updatedValues = {
                    ...formValues,
                    state_select: "",
                    site_select: ""
                };
                setFormValues(updatedValues); */
                debouncedValidation(initialValues);
                //handleMessageBoxContent(EnumUtility.SiteSelectionAlerts.UserNotFound);
            }
        } catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "getLoginUserDetails", {});
        }
    };

    const setUserLoginDetails = async (params) => {
        try {
            const userLoginData = await ApiManager.GetUserLoginFromApi(params);
            SessionManager.SetSession(SessionManager.UserLoginDetailsKey, JSON.stringify(userLoginData));
        } catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "setUserLoginDetails", {});
        }
    };

    const assignStateValue = async (formDetails, stateList, stateID = 0) => {
        try {
            const selectedText = stateList.find((option) => option.orgID === stateID)?.orgName || "";
            const timeZone = stateList.find((option) => option.orgID === stateID)?.systemTimeZoneName;

            sessionService.updateUser({ orgClientID: stateID, stateID: stateID, stateName: selectedText, timeZone: timeZone });

            const updatedValues = {
                ...formDetails,
                state_select: stateID,
                state_name_select: selectedText,
                site_select: "",
                site_name_select: "",
                time_zone_select: timeZone
            };

            UtilityLibrary.updateArrayStateValues(setFormValues, updatedValues);
            return updatedValues;
            //setFormValues(updatedValues);
            //debouncedValidation(updatedValues);
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "assignStateValue", {});
        }
    };

    const assignSiteValue = async (formDetails, siteList, siteID = 0) => {
        try {
            const selectedSite = siteList.find((option) => option.orgID === siteID) || null;
            const selectedSiteName = selectedSite ? selectedSite?.orgName : "";
            const roleID = selectedSite ? selectedSite?.roleDefID : 0;
            sessionService.updateUser({ siteID: siteID, siteName: selectedSiteName, roleID: roleID });

            const updatedValues = {
                ...formDetails,
                site_select: siteID,
                site_name_select: selectedSiteName
            };
            UtilityLibrary.updateArrayStateValues(setFormValues, updatedValues);
            return updatedValues;
            //setFormValues(updatedValues);
            //debouncedValidation(updatedValues);
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "assignSiteValue", {});
        }
    };

    const fetchStates = async (userID = 0, singleStateCheck = false) => {
        try {
            const params = {
                userInfoID: userID
            }
            const orgsListApiData = await ApiManager.GetOrganizationFromApiByUserID(params);
            if (orgsListApiData) {
                if (orgsListApiData.length > 0) {
                    setStateLists(orgsListApiData.sort((a, b) => a.orgName.localeCompare(b.orgName)));
                    setMessageBoxVisible(false);
                    if (singleStateCheck) {
                        if (orgsListApiData.length === 1) {
                            const stateID = orgsListApiData[0].orgID;
                            const updatedValues = await assignStateValue(formValues, orgsListApiData, stateID);
                            const { hasSingleSite, updatedFormValues } = await fetchSites(stateID, updatedValues, true);
                            if (hasSingleSite) {
                                await onSubmitFormDetails(updatedFormValues);
                            }
                        }
                    }
                }
                else {
                    setMessageBoxVisible(true);
                    handleMessageBoxContent(EnumUtility.SiteSelectionAlerts.StateAndSiteAccess);
                }
            }
            else {
                setStateLists([]);
                setMessageBoxVisible(true);
                handleMessageBoxContent(EnumUtility.SiteSelectionAlerts.StateAndSiteAccess);
            }

            setIsSiteSelected(false);
            sessionService.updateUser({ isSiteSelected: false, });

        } catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "fetchStates", {});
        }
    };

    // Update fetchSites function around line 648 in SiteSelector.js
    const fetchSites = async (stateID, values = formValues, singleSiteCheck = false) => {
        try {
            const LoginUserInfo = sessionService.getUser();
            const params = {
                userInfoID: LoginUserInfo.userInfoID,
                parentOrgID: stateID
            };

            const siteListApiData = await ApiManager.GetChildOrganizationFromApiByUserID(params);
            if (siteListApiData) {
                if (siteListApiData.length > 0) {
                    setSiteLists(siteListApiData.sort((a, b) => a.orgName.localeCompare(b.orgName)));
                    setMessageBoxVisible(false);

                    if (singleSiteCheck) {
                        if (siteListApiData.length === 1) {
                            const siteID = siteListApiData[0].orgID;
                            const siteName = siteListApiData[0].orgName;

                            // 🔍 FIX: Update session with the selected site immediately
                            const currentUser = sessionService.getUser();
                            const updatedUser = {
                                ...currentUser,
                                siteID: siteID,
                                siteName: siteName
                            };
                            sessionService.updateUser(updatedUser);


                            const updatedValues = await assignSiteValue(values, siteListApiData, siteID);

                            // 🔍 ADDED: Update form values and trigger validation immediately
                            UtilityLibrary.updateArrayStateValues(setFormValues, updatedValues);

                            // 🔍 ADDED: Force validation to run after site auto-selection
                            setTimeout(() => {
                                console.log('🔍 Triggering validation after single site auto-selection');
                                debouncedValidation(updatedValues);
                            }, 100);

                            return { hasSingleSite: true, updatedFormValues: updatedValues };
                        }
                    }

                    // 🔍 ALSO FIX: If user already has a siteID, make sure it's in session
                    if (values.site_select && values.site_select !== "") {
                        const selectedSite = siteListApiData.find(site => site.orgID === parseInt(values.site_select));
                        if (selectedSite) {
                            const currentUser = sessionService.getUser();
                            const updatedUser = {
                                ...currentUser,
                                siteID: selectedSite.orgID,
                                siteName: selectedSite.orgName
                            };
                            sessionService.updateUser(updatedUser);

                            console.log('🔍 Existing site selection updated in session:', {
                                siteID: selectedSite.orgID,
                                siteName: selectedSite.orgName
                            });
                        }
                    }

                    return { hasSingleSite: false, updatedFormValues: values };
                }
                else {
                    setSiteLists([]);
                    setMessageBoxVisible(true);
                    handleMessageBoxContent(EnumUtility.SiteSelectionAlerts.StateAndSiteAccess);
                    return { hasSingleSite: false, updatedFormValues: values };
                }
            }
            else {
                setSiteLists([]);
                setMessageBoxVisible(true);
                handleMessageBoxContent(EnumUtility.SiteSelectionAlerts.StateAndSiteAccess);
                return { hasSingleSite: false, updatedFormValues: values };
            }
        } catch (error) {
            console.error('🔍 Error in fetchSites:', error);
            setSiteLists([]);
            throw LoggerUtility.ProcessErrorData(error, "fetchSites", {});
        }
    };

    // Update onPageReload around line 647 in SiteSelector.js
    const onPageReload = async () => {
        try {
            const LoginUserInfo = sessionService.getUser();
            if (LoginUserInfo) {
                setLocalLoginUserInfo(LoginUserInfo);
                setSelectedLoginUser(LoginUserInfo.userName);

                await fetchStates(LoginUserInfo.userInfoID);

                // If there's a selected state, also fetch sites
                if (LoginUserInfo.stateID && LoginUserInfo.stateID !== "" && LoginUserInfo.stateID !== 0) {
                    console.log('🔍 User has selected state, fetching sites for stateID:', LoginUserInfo.stateID);
                    await fetchSites(LoginUserInfo.stateID);
                }

                const updatedValues = {
                    ...formValues,
                    state_select: LoginUserInfo.stateID,
                    state_name_select: LoginUserInfo.stateName,
                    site_select: LoginUserInfo.siteID,
                    site_name_select: LoginUserInfo.siteName,
                    time_zone_select: LoginUserInfo.timeZone
                };

                UtilityLibrary.updateArrayStateValues(setFormValues, updatedValues);
                // 🔍 ADDED: Trigger validation after page reload
                setTimeout(() => {
                    console.log('🔍 Triggering validation after page reload');
                    debouncedValidation(updatedValues);
                }, 200);
            }
            else {
                setLocalLoginUserInfo(null);
            }
        } catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "onPageReload", {});
        }
    };


    // //dropdown loading code start.
    // useEffect(() => {
    //     try {
    //         SessionManager.SetSession(SessionManager.CurrentActivePageKey, "/siteselect");
    //         onPageReload();
    //     }
    //     catch (error) {
    //         LoggerUtility.ManageErrorLogging(error, errorFormName, "onPageReload - page load");
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []);

    // Auto-trigger search when user email is populated from token
    useEffect(() => {
        try {
            if (selectedLoginUser && selectedLoginUser.trim() !== "") {
                getLoginUserDetails(selectedLoginUser);
            }
        }
        catch (error) {
            LoggerUtility.ManageErrorLogging(error, errorFormName, "auto-search on email load");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLoginUser]);

    const handleStateChange = async (e) => {
        try {
            const { name, value } = e.target;
            console.log(e.target);
            const updatedValues = await assignStateValue(formValues, stateLists, value);
            const { hasSingleSite, updatedFormValues } = await fetchSites(value, updatedValues, true);
            if (hasSingleSite) {
                debouncedValidation(updatedFormValues);
            }
            else {
                debouncedValidation(updatedValues);
            }
            //fetchSites(value);

        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleStateChange");
        }
    }

    // Update handleSiteChange around line 700 in SiteSelector.js
    const handleSiteChange = async (event) => {
        try {
            const selectedSiteID = event.target.value;
            const selectedSite = siteLists.find(site => site.orgID === parseInt(selectedSiteID));

            if (selectedSite) {
                // 🔍 FIX: Update session immediately when user manually selects a site
                const currentUser = sessionService.getUser();
                const updatedUser = {
                    ...currentUser,
                    siteID: selectedSite.orgID,
                    siteName: selectedSite.orgName
                };
                sessionService.updateUser(updatedUser);

                console.log('🔍 Manual site selection - updated session:', {
                    siteID: selectedSite.orgID,
                    siteName: selectedSite.orgName
                });
            }

            const updatedValues = await assignSiteValue(formValues, siteLists, selectedSiteID);
            UtilityLibrary.updateArrayStateValues(setFormValues, updatedValues);
            debouncedValidation(updatedValues);
        } catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "handleSiteChange", {});
        }
    };

    const checkLoginUserSession = async () => {
        try {
            let clearSessionRequired = true;
            const { LoginUserInfo } = sessionService;
            if (LoginUserInfo && localLoginUserInfo) {
                if (localLoginUserInfo.checkSiteChange) {
                    if (LoginUserInfo.userInfoID === localLoginUserInfo.userInfoID) {
                        if (LoginUserInfo.stateID === localLoginUserInfo.stateID) {
                            if (LoginUserInfo.siteID === localLoginUserInfo.siteID) {
                                clearSessionRequired = false;
                            }
                        }
                    }
                }
            }

            if (clearSessionRequired) {
                let token = '';
                if (sessionStorage.getItem('token')) {
                    token = sessionStorage.getItem('token');
                }
                const loginUserInfo = LoginUserInfo;
                sessionService.clearUser();
                SessionManager.ClearSession();
                sessionService.setUser(loginUserInfo);
                sessionStorage.setItem("token", token);
                sessionStorage.setItem("isSiteSelected", true);
                sessionService.updateUser({ checkSiteChange: false });
                const redirectedFrom = true;
                SessionManager.SetSession(SessionManager.RedirectedFromIDPKey, redirectedFrom);
                setLocalLoginUserInfo(null);
            }
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "checkLoginUserSession", {});
        }
    }

    const handleSubmitClick = async () => {
        try {
            setIsPageLoading(true);

            // 🔍 FIXED: First submit the form to get site settings (including masterServerDBSchema)
            await onSubmitFormDetails(formValues);

            // 🔍 FIXED: Now fetch permissions after site settings are loaded
            const loginUserInfo = sessionService.getUser();
            if (loginUserInfo) {
                // 🔍 FIXED: Get the session data as string first, then parse it
                const siteSettingsDetailsString = SessionManager.GetDataFromSession("SiteSettingsDetailsKey");

                // 🔍 FIXED: Only parse if it's actually a string
                let siteSettingsDetails = null;
                if (siteSettingsDetailsString) {
                    try {
                        siteSettingsDetails = typeof siteSettingsDetailsString === 'string'
                            ? JSON.parse(siteSettingsDetailsString)
                            : siteSettingsDetailsString;
                    } catch (parseError) {
                        console.error('🔍 Error parsing site settings:', parseError);
                        console.log('🔍 Raw site settings data:', siteSettingsDetailsString);
                        siteSettingsDetails = siteSettingsDetailsString; // Use as-is if parsing fails
                    }
                }

                // 🔍 FIXED: Now access the masterDBSchema property
                const masterServerDBSchema = siteSettingsDetails ? siteSettingsDetails.masterDBSchema : null;

                const params = {
                    PageID: 100,
                    UserID: loginUserInfo.legacyUserID || -123456,
                    SiteID: loginUserInfo.siteID || -123456,
                    MasterServerDBSchema: masterServerDBSchema
                };

                const controlSecurityListApiData = await ApiManager.FetchAppBarPermissions(params);
                const actualPermissionsForFields = DeterminePermissions2(controlSecurityListApiData);

                const defaultPermissions = {
                    btnAskPriya: { read: false, write: false, hide: true },
                    btnNext: { read: false, write: false, hide: false },
                    btnNewWorkflow: { read: false, write: false, hide: false },
                    btnNewCallChat: { read: false, write: false, hide: false },
                    btnNewWalkIn: { read: false, write: false, hide: false },
                    btnNewDocument: { read: false, write: false, hide: false },
                    btnQueueWorkflow: { read: false, write: false, hide: false },
                    btnQueueLegal: { read: false, write: false, hide: false },
                    btnQueueWalkIn: { read: false, write: false, hide: false },
                    btnCompletedWorkflow: { read: false, write: false, hide: false },
                    btnCompletedQA: { read: false, write: false, hide: false },
                    btnCompletedVariance: { read: false, write: false, hide: false },
                    btnReporting: { read: false, write: false, hide: false },
                    btnAdmin: { read: false, write: false, hide: false },
                    btnLegal: { read: false, write: false, hide: false },
                    btnView: { read: false, write: false, hide: false },
                    btnViewDocument: { read: false, write: false, hide: false },
                    btnViewCase: { read: false, write: false, hide: false },
                    btnViewContacts: { read: false, write: false, hide: false },
                    btnMyYTracMyProfile: { read: false, write: false, hide: false },
                    btnMyYTracStats: { read: false, write: false, hide: false },
                    btnMyYTracAwards: { read: false, write: false, hide: false },
                    btnMyYTracSystemInfo: { read: false, write: false, hide: false }   

                };

                const mergedPermissions = { ...defaultPermissions, ...actualPermissionsForFields };

                console.log('🔍 SiteSelector storing permissions:', mergedPermissions);
                console.log('🔍 SiteSelector SessionManager about to store:', JSON.stringify(mergedPermissions));

                // 🔍 FIXED: Store permissions as JSON string
                SessionManager.SetSession("UserPermissions", JSON.stringify(mergedPermissions));

                // 🔍 DEBUG: Verify permissions were stored
                const storedPermissions = SessionManager.GetDataFromSession("UserPermissions");
                console.log('🔍 SiteSelector permissions stored successfully:', storedPermissions);


                const firstAvailablePage = getFirstAvailablePage(mergedPermissions);
                // These three pages use enums in currentactivepagekey
                const pageMap = {
                    "/caseview": ClientType.DropdownCategoryType.Case_View,
                    "/newworkitem": ClientType.DropdownCategoryType.NewWorkItem,
                    "/nextworkitem": ClientType.DropdownCategoryType.NextWorkItem
                }
                const currentActivePage = pageMap[firstAvailablePage?.toLowerCase()] ?? firstAvailablePage
                // Set currentactivepagekey based on first available page
                SessionManager.SetSession(SessionManager.CurrentActivePageKey, currentActivePage);
                 

                // 🔍 FIXED: Add a small delay to ensure session storage is updated
                await new Promise(resolve => setTimeout(resolve, 100));

                navigate(firstAvailablePage); //navigate('/newdocument');
            }

        } catch (error) {
            console.error('🔍 Error in SiteSelector submit:', error);
            LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSubmitClick");
        } finally {
            setIsPageLoading(false);
        }
    };

    const handleMessageBoxContent = (messageContent) => {
        try {
            setMessageBoxContent(messageContent);
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "handleMessageBoxContent", {});
        }
    };

    const handleNotificationDialog = (dialogContent) => {
        try {
            setNotificationDialogContent(dialogContent);
            handleOpenNotificationDialog();
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "handleNotificationDialog", {});
        }
    };

    const handleOpenNotificationDialog = () => {
        try {
            setNotificationDialogOpen(true);
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "handleOpenNotificationDialog", {});
        }
    };

    const handleCloseNotificationDialog = useCallback(async () => {
        try {
            setNotificationDialogOpen(false);
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCloseNotificationDialog");
        }
    }, []);

    const tokenValidation = validateToken(sessionStorage.getItem("token"), configLibrary);
    useEffect(() => {
        if (!tokenValidation) {
            window.location.replace("/");
        }
    }, [tokenValidation]);

    return (
        <div style={{ height: '100%' }}>
            <div className='top_div' style={{ backgroundImage: `url(${backgroundImageUrl})` }}>
                <div className='right-area'>
                    <div className="yt-siteselect-container">
                        <div className="yt-siteselect-row yt-siteselect-header-row">
                            <img src='images/ytraclogo.jpg' alt="Logo" className="yt-siteselect-logo" />

                        </div>
                        <div className="yt-siteselect-row yt-siteselect-message-row">
                            <p>Please select a State and Site:</p>
                        </div>
                        {messageBoxVisible &&
                            <div className="yt-siteselect-row yt-siteselect-messageBox-row">
                                <MessageBox messageContent={messageBoxContent} />
                            </div>
                        }
                        <div className="yt-siteselect-row yt-siteselect-inputs-row">
                            <FormControl fullWidth>
                                <TextFieldWithIconButton
                                    nameForField="userNameTxt"
                                    valueForField={selectedLoginUser}
                                    labelForField="User Email"
                                    isDisabled={true}
                                    iconsVisibility={{ search: false, copy: false, clear: false }}
                                />
                            </FormControl>
                        </div>
                        <div className="yt-siteselect-row yt-siteselect-inputs-row">
                            <FormControl
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#5A73EA', // Change border color based on value
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#5A73EA', // Optional hover style
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#5A73EA', // Optional focus style
                                        },
                                    },
                                }}>
                                <InputLabel id="state_selection_label" shrink={true}>State Selection</InputLabel>
                                <Select
                                    labelId="state_selection_label"
                                    id="state_select"
                                    name="state_select"
                                    value={formValues.state_select}
                                    IconComponent={ArrowDropDown}
                                    label="State Selection"
                                    displayEmpty
                                    onChange={handleStateChange}
                                    defaultValue=""
                                >
                                    <MenuItem value="" disabled>Choose One</MenuItem>
                                    {stateLists.map((stateList) => (

                                        <MenuItem key={stateList.orgID} value={stateList.orgID}>
                                            {stateList.orgName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        <div className="yt-siteselect-row yt-siteselect-inputs-row">
                            <FormControl
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#5A73EA', // Change border color based on value
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#5A73EA', // Optional hover style
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#5A73EA', // Optional focus style
                                        },
                                    },
                                }}>
                                <InputLabel id="site_selection_label" shrink={true}>Site Selection</InputLabel>
                                <Select
                                    labelId="site_selection_label"
                                    id="site_select"
                                    name="site_select"
                                    value={formValues.site_select}
                                    IconComponent={ArrowDropDown}
                                    label="Site Selection"
                                    displayEmpty
                                    onChange={handleSiteChange}
                                    defaultValue=""
                                >
                                    <MenuItem value="" disabled>Choose One</MenuItem>
                                    {siteLists.map((siteList) => (

                                        <MenuItem key={siteList.orgID} value={siteList.orgID}>
                                            {siteList.orgName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        <div className="yt-siteselect-row yt-siteselect-inputs-row">
                            <Button name="btnSubmit" color="ywButtonBlue" variant='contained' className='yt-siteselect-action-button ' onClick={handleSubmitClick} disabled={!isSubmitEnabled}>Submit</Button>
                        </div>
                        <div className="yt-siteselect-version">

                            <p>Version: 4.260930.1</p>


                        </div>
                    </div>
                </div>
            </div>


            {/* Include the loader component */}
            <PageLoader isPageLoading={isPageLoading} />

            {/* Include the Notification Dialog component */}
            <NotificationDialog
                openNotification={notificationDialogOpen}
                dialogContent={notificationDialogContent}
                onCloseNotification={handleCloseNotificationDialog}
                isAutoClose={false}
            />

        </div>
    );
};

export default SiteSelector;























